import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Loader2, Brain, ArrowRight, ArrowLeft, Sparkles, Check } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QUESTIONS = {
  es: [
    { key: 'problem', label: 'Cual es el problema que quieres resolver?', placeholder: 'Ej: Las comunidades rurales no tienen acceso a educacion de calidad...' },
    { key: 'beneficiaries', label: 'Para quien es este desafio? (beneficiarios)', placeholder: 'Ej: Estudiantes de zonas rurales, profesores de escuelas publicas...' },
    { key: 'constraints', label: 'Que restricciones o limitaciones existen?', placeholder: 'Ej: Bajo presupuesto, conectividad limitada, necesidad de solucion escalable...' },
    { key: 'desired_outcome', label: 'Que resultado ideal esperas?', placeholder: 'Ej: Una plataforma que permita acceso a contenido educativo sin internet...' },
  ],
  en: [
    { key: 'problem', label: 'What is the problem you want to solve?', placeholder: 'E.g.: Rural communities lack access to quality education...' },
    { key: 'beneficiaries', label: 'Who is this challenge for? (beneficiaries)', placeholder: 'E.g.: Students in rural areas, public school teachers...' },
    { key: 'constraints', label: 'What constraints or limitations exist?', placeholder: 'E.g.: Low budget, limited connectivity, need for scalable solution...' },
    { key: 'desired_outcome', label: 'What ideal outcome do you expect?', placeholder: 'E.g.: A platform that provides access to educational content without internet...' },
  ],
};

export default function HMWAssistant({ open, onClose, onAccept, language = 'es' }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({ problem: '', beneficiaries: '', constraints: '', desired_outcome: '' });
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState('');
  const [error, setError] = useState('');

  const questions = QUESTIONS[language] || QUESTIONS.es;
  const totalSteps = questions.length;

  const t = language === 'es' ? {
    title: 'Asistente IA para tu Desafio',
    subtitle: 'Responde estas preguntas para generar una frase de desafio clara y potente.',
    next: 'Siguiente',
    back: 'Atras',
    generate: 'Generar Desafio',
    generating: 'Generando...',
    resultTitle: 'Tu frase de desafio sugerida:',
    accept: 'Usar esta frase',
    retry: 'Regenerar',
    cancel: 'Cancelar',
    step: 'Pregunta',
    of: 'de',
  } : {
    title: 'AI Assistant for your Challenge',
    subtitle: 'Answer these questions to generate a clear and powerful challenge statement.',
    next: 'Next',
    back: 'Back',
    generate: 'Generate Challenge',
    generating: 'Generating...',
    resultTitle: 'Your suggested challenge statement:',
    accept: 'Use this statement',
    retry: 'Regenerate',
    cancel: 'Cancel',
    step: 'Question',
    of: 'of',
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/ai-assistant/generate-hmw`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...answers, language }),
      });
      if (!res.ok) throw new Error('Failed to generate');
      const data = await res.json();
      setResult(data.hmw);
      setStep(totalSteps);
    } catch (err) {
      setError(language === 'es' ? 'Error al generar. Intenta de nuevo.' : 'Generation error. Try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleAccept = () => {
    onAccept(result);
    handleClose();
  };

  const handleClose = () => {
    setStep(0);
    setAnswers({ problem: '', beneficiaries: '', constraints: '', desired_outcome: '' });
    setResult('');
    setError('');
    onClose();
  };

  const currentQ = questions[step];
  const canProceed = step < totalSteps && answers[currentQ?.key]?.trim().length > 5;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg p-0 overflow-hidden" data-testid="hmw-assistant-dialog">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-violet-600 px-6 py-5 text-white">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2 text-lg">
              <Brain className="w-5 h-5" />
              {t.title}
            </DialogTitle>
          </DialogHeader>
          <p className="text-purple-100 text-sm mt-1">{t.subtitle}</p>
        </div>

        <div className="p-6">
          {/* Progress */}
          {step < totalSteps && (
            <div className="mb-5">
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs font-semibold text-gray-500">{t.step} {step + 1} {t.of} {totalSteps}</span>
              </div>
              <div className="flex gap-1.5">
                {questions.map((_, i) => (
                  <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? 'bg-purple-500' : 'bg-gray-200'}`} />
                ))}
              </div>
            </div>
          )}

          {/* Questions */}
          {step < totalSteps && currentQ && (
            <div className="space-y-3" data-testid={`hmw-step-${step}`}>
              <Label className="text-base font-semibold text-gray-900">{currentQ.label}</Label>
              <Textarea
                value={answers[currentQ.key]}
                onChange={(e) => setAnswers({ ...answers, [currentQ.key]: e.target.value })}
                placeholder={currentQ.placeholder}
                rows={3}
                className="resize-none"
                data-testid={`hmw-input-${currentQ.key}`}
                autoFocus
              />
            </div>
          )}

          {/* Result */}
          {step === totalSteps && result && (
            <div className="space-y-4" data-testid="hmw-result">
              <div className="flex items-center gap-2 text-purple-700">
                <Sparkles className="w-5 h-5" />
                <Label className="text-base font-semibold">{t.resultTitle}</Label>
              </div>
              <div className="p-4 bg-purple-50 border-2 border-purple-200 rounded-lg">
                <p className="text-lg font-medium text-gray-900 leading-relaxed italic">{result}</p>
              </div>
            </div>
          )}

          {error && <p className="text-sm text-red-600 mt-3">{error}</p>}

          {/* Actions */}
          <div className="flex gap-3 mt-6">
            {step > 0 && step < totalSteps && (
              <Button type="button" variant="outline" onClick={() => setStep(step - 1)} className="flex-1">
                <ArrowLeft className="w-4 h-4 mr-1" /> {t.back}
              </Button>
            )}
            {step === 0 && (
              <Button type="button" variant="outline" onClick={handleClose} className="flex-1">
                {t.cancel}
              </Button>
            )}

            {step < totalSteps - 1 && (
              <Button
                type="button"
                onClick={() => setStep(step + 1)}
                disabled={!canProceed}
                className="flex-1 bg-purple-600 hover:bg-purple-700"
                data-testid="hmw-next-btn"
              >
                {t.next} <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            )}

            {step === totalSteps - 1 && (
              <Button
                type="button"
                onClick={handleGenerate}
                disabled={!canProceed || generating}
                className="flex-1 bg-purple-600 hover:bg-purple-700"
                data-testid="hmw-generate-btn"
              >
                {generating ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t.generating}</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-2" /> {t.generate}</>
                )}
              </Button>
            )}

            {step === totalSteps && result && (
              <>
                <Button type="button" variant="outline" onClick={handleGenerate} disabled={generating} className="flex-1" data-testid="hmw-retry-btn">
                  {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : t.retry}
                </Button>
                <Button type="button" onClick={handleAccept} className="flex-1 bg-purple-600 hover:bg-purple-700" data-testid="hmw-accept-btn">
                  <Check className="w-4 h-4 mr-2" /> {t.accept}
                </Button>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
